'use client';

import Loading from '@/app/[locale]/(protected)/loading';
import ROUTES from '@/lib/constants/routeConfig';
import { useAuth } from '@/lib/hooks/useAuth';
import { featureFlagsActions } from '@/lib/store/feature-flags.store';
import { logger } from '@/lib/utils/logger';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect } from 'react';

export default function ProtectedWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const { status, session } = useAuth();
  const router = useRouter();

  const initializeFlagStore = useCallback(async () => {
    try {
      logger.debug('[protected-wrapper] Initializing feature flag store...');
      await featureFlagsActions.initializeFlags(session);
    } catch (error) {
      logger.error(
        '[protected-wrapper] Failed to initialize feature flags via action:',
        error
      );
    }
  }, [session]);

  useEffect(() => {
    if (status === 'unauthenticated') {
      logger.info(
        '[protected-wrapper] User unauthenticated, redirecting to login.'
      );
      // Persist the intended route so we can redirect back after auth
      if (typeof window !== 'undefined' && pathname) {
        sessionStorage.setItem('redirectAfterAuth', pathname);
      }
      router.push(ROUTES.AUTH.LOGIN);
    } else if (status === 'authenticated' && session) {
      initializeFlagStore();
    }
  }, [status, router, session, initializeFlagStore, pathname]);

  if (status === 'authenticated' && session) {
    const isLawyer = session.user.role === 'lawyer';

    // Handle standalone /conversations route by redirecting to role-appropriate route
    if (pathname === '/conversations') {
      const targetRoute = isLawyer
        ? ROUTES.DASHBOARD.CONVERSATIONS
        : ROUTES.MARKETPLACE.CONVERSATIONS;
      router.push(targetRoute);
      return <Loading />;
    }

    if (isLawyer && pathname.startsWith(ROUTES.MARKETPLACE.ROOT)) {
      router.push(ROUTES.DASHBOARD.ROOT);
    } else if (!isLawyer && pathname.startsWith(ROUTES.DASHBOARD.ROOT)) {
      router.push(ROUTES.MARKETPLACE.ROOT);
    } else {
      return children;
    }
  }

  return <Loading />;
}
